/**
 * Translates the AHP action/delta stream and session snapshots into github-ui
 * `SessionEvent`s via a {@link ProjectorSink}.
 *
 * Two entry points share one sink and one set of idempotency state:
 *   - `projectSnapshot` bootstraps history from a subscription snapshot.
 *   - `projectAction` folds each subsequent live action.
 *
 * Switching is done on the AHP wire-literal action/kind values rather than the
 * `ActionType` const enum, which cannot be referenced as a value under
 * `isolatedModules`.
 */

import type {
  ActionEnvelope,
  ToolCallResult,
  SessionState,
  Turn,
  ActiveTurn,
  ResponsePart,
  ToolCallState,
} from '@microsoft/agent-host-protocol'
import type {ProjectorSink} from './projector-sink'

const ACTION_TURN_STARTED = 'session/turnStarted'
const ACTION_RESPONSE_PART = 'session/responsePart'
const ACTION_DELTA = 'session/delta'
const ACTION_REASONING = 'session/reasoning'
const ACTION_TOOL_CALL_START = 'session/toolCallStart'
const ACTION_TOOL_CALL_READY = 'session/toolCallReady'
const ACTION_TOOL_CALL_CONFIRMED = 'session/toolCallConfirmed'
const ACTION_TOOL_CALL_COMPLETE = 'session/toolCallComplete'
const ACTION_TURN_COMPLETE = 'session/turnComplete'
const ACTION_TURN_CANCELLED = 'session/turnCancelled'
const ACTION_ERROR = 'session/error'

const PART_MARKDOWN = 'markdown'
const PART_TOOL_CALL = 'toolCall'

const TOOL_STATUS_PENDING_CONFIRMATION = 'pending-confirmation'
const TOOL_STATUS_PENDING_RESULT_CONFIRMATION = 'pending-result-confirmation'
const TOOL_STATUS_COMPLETED = 'completed'
const TOOL_STATUS_CANCELLED = 'cancelled'

const TURN_STATE_ERROR = 'error'

const TOOL_RESULT_TEXT = 'text'

/** Tool identity captured at `toolCallStart`, reused for later actions. */
interface ToolMeta {
  turnId: string
  toolName: string
  displayName: string
}

/** Extract plain text from a `StringOrMarkdown`-shaped value. */
function plainText(value: unknown): string {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object') {
    const obj = value as {markdown?: unknown; content?: unknown}
    if (typeof obj.markdown === 'string') return obj.markdown
    if (typeof obj.content === 'string') return obj.content
  }
  return ''
}

/** Parse a tool call's raw input into structured arguments (best-effort). */
function parseToolArgs(raw: string | undefined): unknown {
  if (typeof raw !== 'string' || !raw.trim()) return {}
  try {
    return JSON.parse(raw)
  } catch {
    return {input: raw}
  }
}

/** Join the text content blocks of a tool result, falling back to its summary. */
function extractToolResultText(result: ToolCallResult): string {
  const blocks = result.content
  if (Array.isArray(blocks)) {
    const texts: string[] = []
    for (const block of blocks) {
      if (block && typeof block === 'object' && (block as {type?: string}).type === TOOL_RESULT_TEXT) {
        const text = (block as {text?: unknown}).text
        if (typeof text === 'string') texts.push(text)
      }
    }
    const joined = texts.join('\n')
    if (joined) return joined
  }
  return plainText(result.pastTenseMessage)
}

export class AhpActionProjector {
  private readonly emittedUserMessages = new Set<string>()
  private readonly startedTurns = new Set<string>()
  private readonly startedMessages = new Set<string>()
  /** Accumulated markdown text per response-part id, for the persistent final. */
  private readonly markdownText = new Map<string, string>()
  private readonly finalizedMessages = new Set<string>()
  private readonly toolMeta = new Map<string, ToolMeta>()
  private readonly requestedPermissions = new Set<string>()
  private readonly completedPermissions = new Set<string>()
  private readonly requestedTools = new Set<string>()
  private readonly completedTools = new Set<string>()
  private readonly endedTurns = new Set<string>()
  /** The markdown part currently receiving deltas, flushed at the next boundary. */
  private currentMarkdownPartId: string | undefined

  private readonly sink: ProjectorSink

  constructor(sink: ProjectorSink) {
    this.sink = sink
  }

  /** Bootstrap from a subscription snapshot: completed turns, then the active turn. */
  projectSnapshot(state: SessionState): void {
    for (const turn of state.turns ?? []) {
      this.projectCompletedTurn(turn)
    }
    if (state.activeTurn) {
      this.hydrateActiveTurn(state.activeTurn)
    } else if ((state.turns ?? []).length > 0) {
      // A fully-settled history with no active turn is idle.
      this.sink.idle(false)
    }
  }

  /** Translate a single live action envelope into sink calls. */
  projectAction(env: ActionEnvelope): void {
    const action = env.action
    switch (action.type) {
      case ACTION_TURN_STARTED:
        this.emitUserMessage(action.turnId, plainText(action.userMessage?.text))
        this.ensureTurnStarted(action.turnId)
        return

      case ACTION_RESPONSE_PART:
        this.onResponsePart(action.turnId, action.part)
        return

      case ACTION_DELTA:
        this.appendMarkdown(action.partId, action.content)
        return

      case ACTION_REASONING:
        return

      case ACTION_TOOL_CALL_START:
        this.toolMeta.set(action.toolCallId, {
          turnId: action.turnId,
          toolName: action.toolName,
          displayName: action.displayName,
        })
        return

      case ACTION_TOOL_CALL_READY:
        this.onToolReady(action.turnId, action.toolCallId, action.invocationMessage, action.toolInput, action.confirmed)
        return

      case ACTION_TOOL_CALL_CONFIRMED:
        this.onToolConfirmed(
          action.turnId,
          action.toolCallId,
          action.approved,
          action.approved === false ? plainText(action.reasonMessage) || action.reason : undefined,
        )
        return

      case ACTION_TOOL_CALL_COMPLETE:
        this.completeTool(this.buildToolResult(action.turnId, action.toolCallId, action.result))
        return

      case ACTION_TURN_COMPLETE:
        this.finalizeTurn(action.turnId, {aborted: false})
        return

      case ACTION_TURN_CANCELLED:
        this.finalizeTurn(action.turnId, {aborted: true})
        return

      case ACTION_ERROR:
        this.finalizeTurn(action.turnId, {aborted: false, error: action.error?.message || 'unknown error'})
        return

      default:
        return
    }
  }

  private emitUserMessage(turnId: string, content: string): void {
    if (this.emittedUserMessages.has(turnId)) return
    this.emittedUserMessages.add(turnId)
    this.sink.userMessage(turnId, content)
  }

  private ensureTurnStarted(turnId: string): void {
    if (this.startedTurns.has(turnId)) return
    this.startedTurns.add(turnId)
    this.sink.turnStart(turnId)
  }

  private appendMarkdown(partId: string, content: string): void {
    this.setCurrentMarkdown(partId)
    if (!content) return
    this.markdownText.set(partId, (this.markdownText.get(partId) ?? '') + content)
  }

  private onResponsePart(turnId: string, part: ResponsePart): void {
    if (part.kind === PART_MARKDOWN) {
      this.setCurrentMarkdown(part.id, part.content ?? '')
      return
    }
    if (part.kind === PART_TOOL_CALL) {
      // A tool call closes any open assistant text so the timeline keeps order.
      this.flushCurrentMarkdown()
      this.projectToolCallState(turnId, part.toolCall)
    }
  }

  /** Mark `partId` as the open markdown part, flushing any previously-open one. */
  private setCurrentMarkdown(partId: string, seed?: string): void {
    if (this.currentMarkdownPartId && this.currentMarkdownPartId !== partId) {
      this.flushMarkdownPart(this.currentMarkdownPartId)
    }
    this.currentMarkdownPartId = partId
    this.startedMessages.add(partId)
    if (seed !== undefined && !this.markdownText.has(partId)) {
      this.markdownText.set(partId, seed)
    }
  }

  private flushMarkdownPart(partId: string): void {
    if (this.currentMarkdownPartId === partId) this.currentMarkdownPartId = undefined
    const content = this.markdownText.get(partId)
    if (!content || this.finalizedMessages.has(partId)) return
    this.finalizedMessages.add(partId)
    this.sink.messageFinal(partId, content)
  }

  private flushCurrentMarkdown(): void {
    if (this.currentMarkdownPartId) this.flushMarkdownPart(this.currentMarkdownPartId)
  }

  private onToolReady(
    turnId: string,
    toolCallId: string,
    invocationMessage: unknown,
    toolInput: string | undefined,
    confirmed: string | undefined,
  ): void {
    // Close any open assistant text so it renders before the tool entry.
    this.flushCurrentMarkdown()
    if (!confirmed && !this.requestedPermissions.has(toolCallId)) {
      this.requestedPermissions.add(toolCallId)
      const meta = this.toolMeta.get(toolCallId)
      this.sink.permissionRequested({
        requestId: toolCallId,
        turnId,
        toolCallId,
        toolName: meta?.displayName || meta?.toolName || 'tool',
        detail: plainText(invocationMessage),
      })
    }
    this.ensureToolRequested(turnId, toolCallId, toolInput)
  }

  private onToolConfirmed(turnId: string, toolCallId: string, approved: boolean, denialMessage?: string): void {
    if (!this.completedPermissions.has(toolCallId)) {
      this.completedPermissions.add(toolCallId)
      this.sink.permissionCompleted({requestId: toolCallId, toolCallId, approved})
    }
    // A denial transitions the tool straight to cancelled with no later
    // `toolCallComplete`, so surface its terminal failure here.
    if (!approved) {
      this.completeTool({
        toolCallId,
        turnId,
        success: false,
        content: '',
        errorMessage: denialMessage || 'Tool call was cancelled',
      })
    }
  }

  private ensureToolRequested(turnId: string, toolCallId: string, toolInput: string | undefined): void {
    if (this.requestedTools.has(toolCallId)) return
    this.requestedTools.add(toolCallId)
    this.flushCurrentMarkdown()
    const meta = this.toolMeta.get(toolCallId)
    this.sink.toolRequested({
      toolCallId,
      turnId,
      toolName: meta?.toolName || 'tool',
      toolTitle: meta?.displayName || undefined,
      args: parseToolArgs(toolInput),
    })
  }

  private completeTool(result: {
    toolCallId: string
    turnId: string
    success: boolean
    content: string
    detailedContent?: string
    errorMessage?: string
  }): void {
    if (this.completedTools.has(result.toolCallId)) return
    this.completedTools.add(result.toolCallId)
    this.ensureToolRequested(result.turnId, result.toolCallId, undefined)
    this.sink.toolCompleted(result)
  }

  private buildToolResult(
    turnId: string,
    toolCallId: string,
    result: ToolCallResult,
  ): {
    toolCallId: string
    turnId: string
    success: boolean
    content: string
    detailedContent?: string
    errorMessage?: string
  } {
    const text = extractToolResultText(result)
    if (result.success === false) {
      return {
        toolCallId,
        turnId,
        success: false,
        content: '',
        errorMessage: result.error?.message || text || 'Tool call failed',
      }
    }
    return {toolCallId, turnId, success: true, content: text, detailedContent: text || undefined}
  }

  private finalizeTurn(turnId: string, opts: {aborted: boolean; error?: string}): void {
    if (this.endedTurns.has(turnId)) return
    this.endedTurns.add(turnId)
    this.ensureTurnStarted(turnId)
    this.flushMarkdownFinals()
    if (opts.error !== undefined) {
      this.sink.turnError(turnId, opts.error)
    }
    this.sink.turnEnd(turnId)
    this.sink.idle(opts.aborted)
  }

  private flushMarkdownFinals(): void {
    this.currentMarkdownPartId = undefined
    for (const [partId, content] of this.markdownText) {
      if (!content || this.finalizedMessages.has(partId)) continue
      this.finalizedMessages.add(partId)
      this.startedMessages.add(partId)
      this.sink.messageFinal(partId, content)
    }
  }

  // ─── Snapshot bootstrap ────────────────────────────────────────────────────

  private projectCompletedTurn(turn: Turn): void {
    this.emitUserMessage(turn.id, plainText(turn.userMessage?.text))
    this.ensureTurnStarted(turn.id)
    for (const part of turn.responseParts ?? []) {
      this.projectResponsePart(turn.id, part, {active: false})
    }
    this.flushCurrentMarkdown()
    if (this.endedTurns.has(turn.id)) return
    this.endedTurns.add(turn.id)
    if (turn.state === TURN_STATE_ERROR) {
      this.sink.turnError(turn.id, turn.error?.message || 'unknown error')
    }
    this.sink.turnEnd(turn.id)
  }

  private hydrateActiveTurn(turn: ActiveTurn): void {
    this.emitUserMessage(turn.id, plainText(turn.userMessage?.text))
    this.ensureTurnStarted(turn.id)
    for (const part of turn.responseParts ?? []) {
      // Leave the trailing markdown part open so live deltas accumulate onto it
      // and flush once at the live turn-complete.
      this.projectResponsePart(turn.id, part, {active: true})
    }
  }

  private projectResponsePart(turnId: string, part: ResponsePart, opts: {active: boolean}): void {
    if (part.kind === PART_MARKDOWN) {
      this.setCurrentMarkdown(part.id, part.content ?? '')
      if (!opts.active) this.flushMarkdownPart(part.id)
      return
    }
    if (part.kind === PART_TOOL_CALL) {
      this.flushCurrentMarkdown()
      this.projectToolCallState(turnId, part.toolCall)
    }
  }

  private projectToolCallState(turnId: string, tc: ToolCallState): void {
    this.toolMeta.set(tc.toolCallId, {turnId, toolName: tc.toolName, displayName: tc.displayName})
    const toolInput = (tc as {toolInput?: string}).toolInput
    if (tc.status === TOOL_STATUS_PENDING_CONFIRMATION && !this.requestedPermissions.has(tc.toolCallId)) {
      this.requestedPermissions.add(tc.toolCallId)
      this.sink.permissionRequested({
        requestId: tc.toolCallId,
        turnId,
        toolCallId: tc.toolCallId,
        toolName: tc.displayName || tc.toolName || 'tool',
        detail: plainText((tc as {invocationMessage?: unknown}).invocationMessage),
      })
    }
    this.ensureToolRequested(turnId, tc.toolCallId, toolInput)
    const terminal =
      tc.status === TOOL_STATUS_COMPLETED ||
      tc.status === TOOL_STATUS_PENDING_RESULT_CONFIRMATION ||
      tc.status === TOOL_STATUS_CANCELLED
    // A snapshot can arrive (e.g. after reconnect) with a tool already resolved
    // whose permission prompt was emitted earlier; clear it so the UI settles.
    if (terminal && this.requestedPermissions.has(tc.toolCallId) && !this.completedPermissions.has(tc.toolCallId)) {
      this.completedPermissions.add(tc.toolCallId)
      this.sink.permissionCompleted({
        requestId: tc.toolCallId,
        toolCallId: tc.toolCallId,
        approved: tc.status !== TOOL_STATUS_CANCELLED,
      })
    }
    // NOTE: `pending-result-confirmation` is surfaced as completed in v1; result
    // approval/denial is not yet modeled.
    if (tc.status === TOOL_STATUS_COMPLETED || tc.status === TOOL_STATUS_PENDING_RESULT_CONFIRMATION) {
      this.completeTool(this.buildToolResult(turnId, tc.toolCallId, tc))
    } else if (tc.status === TOOL_STATUS_CANCELLED) {
      this.completeTool({
        toolCallId: tc.toolCallId,
        turnId,
        success: false,
        content: '',
        errorMessage: plainText((tc as {reasonMessage?: unknown}).reasonMessage) || 'Tool call was cancelled',
      })
    }
  }
}
